import { Link, useLocation, useSearchParams } from 'react-router-dom';

import {
  FetchLoading,
  InfiniteScrollSentinel,
  List,
  PullToRefresh,
} from '@/shared/component';
import { QUERY_KEY, STALE_TIME } from '@/shared/constant';
import { useNewBoard, useSuspenseInfiniteScroll } from '@/shared/hook';
import { getBoard, getBoardTitleToTextId, isNewBoardPost } from '@/shared/lib';

import { PostBar } from '@/feature/board/component';
import { usePostListFilter } from '@/feature/board/hook/usePostListFilter';

import { getEventPosts, getPosts } from '@/apis';

import styles from './PostList.module.css';

export default function PostList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const isNewBoardOn = useNewBoard();

  const currentBoardTextId = pathname.split('/')[2];
  const currentBoard = getBoard(currentBoardTextId);
  const isBesookt = currentBoardTextId === 'besookt' ? true : false;
  const isEvent = currentBoardTextId === 'event' ? true : false;
  const progressType = searchParams.get('progressType') ?? 'ALL';
  const { sort, year, category } = usePostListFilter(currentBoard.id);
  const isFiltered = Boolean(year || category);

  // 페이지네이션 관련 hook (일반 / 이벤트 )
  const {
    items: postList,
    ref,
    isFetchingNextPage,
    refetch,
  } = useSuspenseInfiniteScroll({
    queryKey: isEvent
      ? [QUERY_KEY.events, currentBoard.id, progressType]
      : [
          QUERY_KEY.posts,
          currentBoard.id,
          sort ?? 'LATEST',
          year ?? 'ALL',
          category ?? 'ALL',
        ],
    queryFn: ({ pageParam }) =>
      isEvent
        ? getEventPosts({
            page: pageParam,
            progressType: progressType === 'ALL' ? undefined : progressType,
          })
        : getPosts(currentBoard.id, pageParam, { sort, year, category }),
    staleTime: STALE_TIME.boardPostList,
    getItemKey: (item) => item.postId,
  });

  // 미노출 게시판 게시글이 서버 응답에 섞여 들어오는 경우를 차단
  const visiblePostList = isNewBoardOn
    ? postList
    : postList.filter((post) => !isNewBoardPost(post));

  if (!visiblePostList.length) {
    return (
      <FetchLoading animation={false}>
        {isEvent
          ? progressType === 'ALL'
            ? '게시물이 없어요'
            : '해당 상태의 이벤트가 없어요'
          : isFiltered
            ? '조건에 맞는 게시물이 없어요'
            : '게시물이 없어요'}
      </FetchLoading>
    );
  }

  return (
    <div>
      <PullToRefresh onRefresh={refetch}>
        <List>
          {visiblePostList.map((post) => (
            <Link
              className={styles.to}
              key={post.postId}
              to={
                isBesookt
                  ? `/board/${getBoardTitleToTextId(post.boardName)}/post/${post.postId}`
                  : `/board/${currentBoardTextId}/post/${post.postId}`
              }
            >
              <PostBar {...post}>
                {post.boardName && (
                  <PostBar.Chip name={post.boardName} variant='grey' />
                )}
              </PostBar>
            </Link>
          ))}

          <InfiniteScrollSentinel ref={ref} />
          {isFetchingNextPage && <FetchLoading />}
        </List>
      </PullToRefresh>
    </div>
  );
}
